import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import api from "../api/axios";



const emptyForm = {
  name: "",
  payment_qr: null,
  whatsapp_number: "",
  callingNumber: "",
  active: true,
  upiId: ""
};

const ProviderManagement = () => {
  const [providers, setProviders] = useState([]);
  const [allBorders, setAllBorders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyBorderIds, setBusyBorderIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [providerForm, setProviderForm] = useState(emptyForm);
  const [editingProviderId, setEditingProviderId] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);

  const [pendingBorderIds, setPendingBorderIds] = useState([]); // create-mode staging
  const [checkedAvailable, setCheckedAvailable] = useState([]);
  const [checkedAssigned, setCheckedAssigned] = useState([]);

  const [search, setSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");

  // ---- Transfer All ----
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferSource, setTransferSource] = useState(null); // provider obj
  const [transferTargetId, setTransferTargetId] = useState("");
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    fetchProviders();
    fetchAllBorders();
  }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const res = await api.get("/provider");
      if (res.data?.success) setProviders(res.data.data || []);
    } catch {
      Swal.fire("Error", "Failed to load providers", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBorders = async () => {
    try {
      const res = await api.get(`/admin/border-tax`);
      if (res.data?.success) setAllBorders(res.data.data || []);
    } catch {
      console.error("Failed to load borders");
    }
  };

  const refreshEditingProvider = useCallback(async (providerId) => {
    const res = await api.get("/provider");
    if (res.data?.success) {
      const list = res.data.data || [];
      setProviders(list);
      const fresh = list.find((p) => p._id === providerId);
      if (fresh) setEditingProvider(fresh);
    }
  }, []);

  const openProviderModal = (provider = null) => {
    setSearch("");
    setPendingBorderIds([]);
    setCheckedAvailable([]);
    setCheckedAssigned([]);
    if (provider) {
      setEditingProviderId(provider._id);
      setEditingProvider(provider);
      setProviderForm({

        name: provider.name || "",
        callingNumber: provider.callingNumber || "",
        whatsapp_number: provider.whatsapp_number || "",
        payment_qr: provider.payment_qr || null,
        active: !!provider.active,
        upiId: provider.upiId || null,
      });
    } else {
      setEditingProviderId(null);
      setEditingProvider(null);
      setProviderForm(emptyForm);
    }
    setShowProviderModal(true);
  };

  const closeProviderModal = () => {
    setShowProviderModal(false);
    setEditingProviderId(null);
    setEditingProvider(null);
    setPendingBorderIds([]);
    setCheckedAvailable([]);
    setCheckedAssigned([]);
    setSearch("");
  };

  const handleProviderChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    setProviderForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : type === "file"
            ? files?.[0] || null
            : value,
    }));
  };

  const markBusy = (ids, on) =>
    setBusyBorderIds((prev) =>
      on
        ? [...new Set([...prev, ...ids])]
        : prev.filter((i) => !ids.includes(i)),
    );

  const toggle = (setter) => (id) =>
    setter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAvailable = toggle(setCheckedAvailable);
  const toggleAssigned = toggle(setCheckedAssigned);

  // single API call, array payload — server handles batch, no doc race
  const assignBorders = (providerId, borderIds) =>
    api.post(`/provider/${providerId}/assign-borders`, { borderIds });

  const removeOne = (providerId, borderId) =>
    api.delete(`/provider/${providerId}/remove-border/${borderId}`);

  // remove endpoint is per-border and mutates same doc → run in small serial chunks,
  // parallel Promise.all on same doc caused overwrite/version failures
  const removeBordersSafe = async (providerId, borderIds) => {
    const failed = [];
    for (const id of borderIds) {
      try {
        await removeOne(providerId, id);
      } catch {
        failed.push(id);
      }
    }
    return failed;
  };

  // ---- ADD selected / all ----
  const runAdd = async (ids) => {
    if (!ids.length) return;

    if (!editingProviderId) {
      setPendingBorderIds((prev) => [...new Set([...prev, ...ids])]);
      setCheckedAvailable([]);
      return;
    }

    setBulkBusy(true);
    markBusy(ids, true);
    try {
      await assignBorders(editingProviderId, ids);
      await refreshEditingProvider(editingProviderId);
      setCheckedAvailable([]);
      Swal.fire({
        icon: "success",
        title: "Assigned",
        text: `${ids.length} border(s) added`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Assign failed",
        "error",
      );
    } finally {
      markBusy(ids, false);
      setBulkBusy(false);
    }
  };

  // ---- REMOVE selected / all ----
  const runRemove = async (ids, confirmText) => {
    if (!ids.length) return;

    if (!editingProviderId) {
      setPendingBorderIds((prev) => prev.filter((id) => !ids.includes(id)));
      setCheckedAssigned([]);
      return;
    }

    const confirmed = await Swal.fire({
      title: "Remove borders?",
      text: confirmText,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, remove",
    });
    if (!confirmed.isConfirmed) return;

    setBulkBusy(true);
    markBusy(ids, true);
    try {
      const failed = await removeBordersSafe(editingProviderId, ids);
      await refreshEditingProvider(editingProviderId);
      setCheckedAssigned([]);
      Swal.fire({
        icon: failed.length ? "warning" : "success",
        title: failed.length ? "Partial" : "Removed",
        text: `${ids.length - failed.length} removed${failed.length ? `, ${failed.length} failed` : ""
          }`,
        timer: 1600,
      });
    } finally {
      markBusy(ids, false);
      setBulkBusy(false);
    }
  };
  const handleProviderSubmit = async (e) => {
    e.preventDefault();

    if (
      !providerForm.name.trim() ||
      !providerForm.whatsapp_number.trim()
    ) {
      Swal.fire(
        "Required",
        "Name and WhatsApp number are mandatory",
        "warning"
      );
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();

      formData.append("name", providerForm.name.trim());
      formData.append("upiId", providerForm.upiId.trim());

      formData.append(
        "whatsapp_number",
        providerForm.whatsapp_number.trim()
      );
      formData.append(
        "callingNumber",
        providerForm.callingNumber.trim()
      );
      formData.append("active", providerForm.active);

      // Add QR only when user selects a new file
      if (providerForm.payment_qr) {
        formData.append(
          "payment_qr",
          providerForm.payment_qr
        );
      }

      let res;

      if (editingProviderId) {
        // UPDATE
        res = await api.put(
          `/provider/${editingProviderId}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      } else {
        // CREATE
        res = await api.post(
          "/provider",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      if (res.data?.success) {
        const savedId =
          editingProviderId || res.data.data?._id;

        // Create mode: assign pending borders
        if (
          !editingProviderId &&
          savedId &&
          pendingBorderIds.length
        ) {
          await assignBorders(
            savedId,
            pendingBorderIds
          );
        }

        Swal.fire({
          icon: "success",
          title: "Success",
          text: editingProviderId
            ? "Provider updated successfully"
            : "Provider created successfully",
          timer: 1500,
          showConfirmButton: false,
        });

        await fetchProviders();
        closeProviderModal();
      }
    } catch (err) {
      console.error("Provider save error:", err);

      Swal.fire(
        "Error",
        err.response?.data?.message ||
        "Failed to save provider",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async (id, name) => {
    const confirm = await Swal.fire({
      title: "Delete Provider?",
      text: `"${name}" will be permanently deleted. This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, Delete",
    });
    if (!confirm.isConfirmed) return;

    try {
      const res = await api.delete(`/provider/${id}`);
      if (res.data?.success) {
        Swal.fire("Deleted", "Provider removed successfully", "success");
        fetchProviders();
      }
    } catch {
      Swal.fire("Error", "Failed to delete provider", "error");
    }
  };

  // ---- Transfer All handlers ----
  const openTransferModal = (provider) => {
    if (!provider.assign_borders?.length) return;
    setTransferSource(provider);
    setTransferTargetId("");
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    setTransferSource(null);
    setTransferTargetId("");
  };

  const handleTransferAll = async () => {
    if (!transferSource || !transferTargetId) return;

    const borderIds = (transferSource.assign_borders || []).map((b) => b._id);
    if (!borderIds.length) return;

    const targetProvider = providers.find((p) => p._id === transferTargetId);

    const confirmed = await Swal.fire({
      title: "Transfer all borders?",
      text: `${borderIds.length} border(s) from "${transferSource.name}" will move to "${targetProvider?.name || "selected provider"}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      confirmButtonText: "Yes, transfer",
    });
    if (!confirmed.isConfirmed) return;

    setTransferring(true);
    markBusy(borderIds, true);
    try {
      // assign→target (single batch call) + remove→source (serial, same-doc safe)
      // different docs, so run concurrently via Promise.all
      const [, failedRemoves] = await Promise.all([
        assignBorders(transferTargetId, borderIds),
        removeBordersSafe(transferSource._id, borderIds),
      ]);

      await fetchProviders();

      if (editingProviderId === transferSource._id || editingProviderId === transferTargetId) {
        await refreshEditingProvider(editingProviderId);
      }

      const failedCount = failedRemoves?.length || 0;
      Swal.fire({
        icon: failedCount ? "warning" : "success",
        title: failedCount ? "Partial transfer" : "Transferred",
        text: `${borderIds.length - failedCount} border(s) transferred${failedCount ? `, ${failedCount} failed to unassign from source` : ""
          }`,
        timer: 1800,
      });

      closeTransferModal();
    } catch (err) {
      Swal.fire(
        "Error",
        err.response?.data?.message || "Transfer failed",
        "error",
      );
    } finally {
      markBusy(borderIds, false);
      setTransferring(false);
    }
  };

  const borderMap = useMemo(() => {
    const m = new Map();
    allBorders.forEach((b) => m.set(b._id, b));
    return m;
  }, [allBorders]);

  const assignedBorderIdsGlobal = useMemo(
    () =>
      new Set(
        providers.flatMap((p) => (p.assign_borders || []).map((b) => b._id)),
      ),
    [providers],
  );

  const assignedList = useMemo(() => {
    if (editingProviderId) return editingProvider?.assign_borders || [];
    return pendingBorderIds.map((id) => borderMap.get(id)).filter(Boolean);
  }, [editingProviderId, editingProvider, pendingBorderIds, borderMap]);

  const assignedIds = useMemo(
    () => assignedList.map((b) => b._id),
    [assignedList],
  );

  const availableBorders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allBorders
      .filter((b) => !assignedBorderIdsGlobal.has(b._id))
      .filter((b) => !pendingBorderIds.includes(b._id))
      .filter((b) => !q || (b.state || "").toLowerCase().includes(q));
  }, [allBorders, assignedBorderIdsGlobal, pendingBorderIds, search]);

  const availableIds = useMemo(
    () => availableBorders.map((b) => b._id),
    [availableBorders],
  );

  const allAvailableChecked =
    availableIds.length > 0 &&
    availableIds.every((id) => checkedAvailable.includes(id));
  const allAssignedChecked =
    assignedIds.length > 0 &&
    assignedIds.every((id) => checkedAssigned.includes(id));

  const filteredProviders = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.whatsapp_number || "").includes(q) ||
        (p.callingNumber || "").includes(q),
    );
  }, [providers, tableSearch]);

  // eligible transfer targets: everyone except the source provider
  const transferTargets = useMemo(
    () => providers.filter((p) => p._id !== transferSource?._id),
    [providers, transferSource],
  );

  const Pill = ({ children, tone = "slate" }) => {
    const tones = {
      green: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
      red: "bg-rose-50 text-rose-700 ring-rose-600/20",
      slate: "bg-slate-100 text-slate-700 ring-slate-500/20",
      indigo: "bg-indigo-50 text-indigo-700 ring-indigo-600/20",
    };
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ring-inset ${tones[tone]}`}
      >
        {children}
      </span>
    );
  };

  const BorderRow = ({ border, checked, onToggle, busy, accent }) => (
    <label
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border transition cursor-pointer ${checked
        ? accent === "indigo"
          ? "border-indigo-400 bg-indigo-50/40"
          : "border-rose-400 bg-rose-50/40"
        : "border-slate-200 hover:border-slate-300"
        } ${busy ? "opacity-50 pointer-events-none" : ""}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(border._id)}
        className={`h-4.5 w-4.5 rounded focus:ring-2 ${accent === "indigo"
          ? "text-indigo-600 focus:ring-indigo-500"
          : "text-rose-600 focus:ring-rose-500"
          }`}
      />
      <span className="text-sm text-slate-700 truncate flex-1">
        {border.state}
        {border.is_based_onDay && (
          <span className="ml-2 text-xs text-slate-400">day-based</span>
        )}
      </span>
      {busy && <span className="text-xs text-slate-400">...</span>}
    </label>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
              Provider Management
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Manage border tax providers and their assigned borders.
            </p>
          </div>
          <button
            onClick={() => openProviderModal()}
            className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 text-white text-sm font-semibold rounded-xl shadow-sm hover:bg-indigo-700 active:scale-[.98] transition"
          >
            <span className="text-lg leading-none">+</span> New Provider
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Providers", value: providers.length },
            { label: "Active", value: providers.filter((p) => p.active).length },
            { label: "Total Borders", value: allBorders.length },
            {
              label: "Unassigned",
              value: allBorders.length - assignedBorderIdsGlobal.size,
            },
          ].map((s) => (
            <div
              key={s.label}
              className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {s.label}
              </p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{s.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white shadow-sm rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <h2 className="text-sm font-semibold text-slate-800">
              All Providers
            </h2>
            <input
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
              placeholder="Search name or number..."
              className="w-full sm:w-72 px-3.5 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {["Name", "WhatsApp", "Calling", "Status", "Borders", ""].map(
                    (h, i) => (
                      <th
                        key={h + i}
                        className={`px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide ${i === 5 ? "text-right" : "text-left"
                          }`}
                      >
                        {h || "Actions"}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  [...Array(4)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(6)].map((__, j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-slate-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredProviders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-16 text-center">
                      <p className="text-slate-900 font-medium">
                        No providers found
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        Create a provider to get started.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredProviders.map((p) => (
                    <tr key={p._id} className="hover:bg-slate-50 transition">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">
                            {(p.name || "?").charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium text-slate-900">
                            {p.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        {p.whatsapp_number || "—"}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-600">
                        {p.callingNumber || "—"}
                      </td>
                      <td className="px-5 py-4">
                        <Pill tone={p.active ? "green" : "red"}>
                          {p.active ? "Active" : "Inactive"}
                        </Pill>
                      </td>
                      <td className="px-5 py-4">
                        <Pill tone="indigo">
                          {p.assign_borders?.length || 0} assigned
                        </Pill>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => openTransferModal(p)}
                          disabled={!p.assign_borders?.length}
                          title={
                            !p.assign_borders?.length
                              ? "No borders assigned"
                              : "Transfer all assigned borders to another provider"
                          }
                          className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition disabled:text-slate-300 disabled:hover:bg-transparent"
                        >
                          Transfer All
                        </button>
                        <button
                          onClick={() => openProviderModal(p)}
                          className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProvider(p._id, p.name)}
                          className="ml-1 px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 rounded-lg transition"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Transfer All modal */}
      {showTransferModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Transfer All Borders
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  From <span className="font-semibold">{transferSource?.name}</span> (
                  {transferSource?.assign_borders?.length || 0} assigned)
                </p>
              </div>
              <button
                type="button"
                onClick={closeTransferModal}
                className="h-9 w-9 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Transfer to <span className="text-rose-500">*</span>
                </label>
                <select
                  value={transferTargetId}
                  onChange={(e) => setTransferTargetId(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="">Select provider...</option>
                  {transferTargets.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name} ({p.assign_borders?.length || 0} assigned)
                    </option>
                  ))}
                </select>
              </div>

              {!transferTargets.length && (
                <p className="text-sm text-rose-500">
                  No other provider available to transfer to.
                </p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeTransferModal}
                className="px-5 py-2.5 text-sm font-semibold border border-slate-300 rounded-xl text-slate-700 bg-white hover:bg-slate-100 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTransferAll}
                disabled={!transferTargetId || transferring}
                className="px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 transition"
              >
                {transferring ? "Transferring..." : "Transfer All"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProviderModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  {editingProviderId
                    ? `Edit: ${editingProvider?.name || ""}`
                    : "New Provider"}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {editingProviderId
                    ? "Tick borders, then Add / Remove selected."
                    : "Borders assign after provider is created."}
                </p>
              </div>
              <button
                type="button"
                onClick={closeProviderModal}
                className="h-9 w-9 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition text-xl leading-none"
              >
                ×
              </button>
            </div>

            <form
              onSubmit={handleProviderSubmit}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      name="name"
                      value={providerForm.name}
                      onChange={handleProviderChange}
                      required
                      placeholder="Provider name"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Provider Upi Id <span className="text-rose-500">*</span>
                    </label>
                    <input
                      name="upiId"
                      value={providerForm.upiId}
                      onChange={handleProviderChange}
                      required
                      placeholder="Provider upi Id"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      WhatsApp Number <span className="text-rose-500">*</span>
                    </label>
                    <input
                      name="whatsapp_number"
                      value={providerForm.whatsapp_number}
                      onChange={handleProviderChange}
                      required
                      placeholder="91XXXXXXXXXX"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Calling Number
                    </label>
                    <input
                      name="callingNumber"
                      value={providerForm.callingNumber}
                      onChange={handleProviderChange}
                      placeholder="Optional"
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Payment QR
                    </label>

                    <input
                      type="file"
                      name="payment_qr"
                      accept="image/*"
                      onChange={handleProviderChange}
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-xl
               focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
               outline-none bg-white"
                    />

                    {editingProvider?.payment_qr && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-1">
                          Current Payment QR
                        </p>

                        <img
                          src={editingProvider.payment_qr}
                          alt="Payment QR"
                          className="h-24 w-24 object-contain rounded-lg border border-slate-200"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <label className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200 cursor-pointer">
                  <input
                    type="checkbox"
                    name="active"
                    checked={providerForm.active}
                    onChange={handleProviderChange}
                    className="h-5 w-5 rounded text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Active provider
                  </span>
                </label>

                <div className="grid lg:grid-cols-2 gap-4">
                  {/* Assigned panel */}
                  <div className="border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-slate-800">
                          Assigned Borders
                        </h3>
                        <span className="text-xs text-slate-500">
                          {checkedAssigned.length}/{assignedIds.length} selected
                        </span>
                      </div>
                      <div className="mt-2.5 flex items-center gap-2">
                        <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer px-2 py-1.5 rounded-lg hover:bg-slate-100">
                          <input
                            type="checkbox"
                            checked={allAssignedChecked}
                            onChange={() =>
                              setCheckedAssigned(
                                allAssignedChecked ? [] : assignedIds,
                              )
                            }
                            disabled={!assignedIds.length}
                            className="h-4 w-4 rounded text-rose-600 focus:ring-rose-500"
                          />
                          Select all
                        </label>
                        <button
                          type="button"
                          disabled={!checkedAssigned.length || bulkBusy}
                          onClick={() =>
                            runRemove(
                              checkedAssigned,
                              `${checkedAssigned.length} border(s) will be unassigned.`,
                            )
                          }
                          className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:bg-slate-200 disabled:text-slate-400 transition"
                        >
                          {bulkBusy ? "..." : `Remove Selected`}
                        </button>
                      </div>
                    </div>

                    <div className="p-2 space-y-1.5 max-h-72 overflow-y-auto min-h-[160px]">
                      {assignedIds.length === 0 ? (
                        <div className="flex items-center justify-center py-14">
                          <p className="text-sm text-slate-400">
                            No borders assigned yet
                          </p>
                        </div>
                      ) : (
                        assignedList.map((border) => (
                          <BorderRow
                            key={border._id}
                            border={border}
                            accent="rose"
                            checked={checkedAssigned.includes(border._id)}
                            onToggle={toggleAssigned}
                            busy={busyBorderIds.includes(border._id)}
                          />
                        ))
                      )}
                    </div>
                  </div>

                  {/* Available panel */}
                  <div className="border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-semibold text-slate-800">
                          Available Borders
                        </h3>
                        <span className="text-xs text-slate-500">
                          {checkedAvailable.length}/{availableIds.length} selected
                        </span>
                      </div>
                      <div className="mt-2.5 flex items-center gap-2">
                        <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer px-2 py-1.5 rounded-lg hover:bg-slate-100">
                          <input
                            type="checkbox"
                            checked={allAvailableChecked}
                            onChange={() =>
                              setCheckedAvailable(
                                allAvailableChecked ? [] : availableIds,
                              )
                            }
                            disabled={!availableIds.length}
                            className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                          />
                          Select all
                        </label>
                        <button
                          type="button"
                          disabled={!checkedAvailable.length || bulkBusy}
                          onClick={() => runAdd(checkedAvailable)}
                          className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition"
                        >
                          {bulkBusy ? "..." : "Add Selected"}
                        </button>
                      </div>
                    </div>

                    <div className="px-3 pt-3">
                      <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search state..."
                        className="w-full px-3.5 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>

                    <div className="p-2 space-y-1.5 max-h-60 overflow-y-auto min-h-[120px]">
                      {availableBorders.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                          <p className="text-sm text-slate-400">
                            No available borders
                          </p>
                        </div>
                      ) : (
                        availableBorders.map((border) => (
                          <BorderRow
                            key={border._id}
                            border={border}
                            accent="indigo"
                            checked={checkedAvailable.includes(border._id)}
                            onToggle={toggleAvailable}
                            busy={busyBorderIds.includes(border._id)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeProviderModal}
                  className="px-5 py-2.5 text-sm font-semibold border border-slate-300 rounded-xl text-slate-700 bg-white hover:bg-slate-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 text-sm font-semibold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 transition"
                >
                  {saving
                    ? "Saving..."
                    : editingProviderId
                      ? "Update Provider"
                      : "Create Provider"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderManagement;